import { useCallback, useEffect, useRef, useState } from "react";
import { visaoGeralApi } from "./api";

/**
 * Dados da Visão Geral com cache EM MEMÓRIA (regra do dono, 23/10/2026):
 *  - voltar para a aba sem recarregar o painel mostra na hora o que já tinha;
 *  - F5, fechar e abrir: a memória some e tudo é buscado de novo, com a
 *    animação de carregamento nos cartões;
 *  - VENDAS DE HOJE se atualiza sozinho a cada 60 s (rota leve /hoje),
 *    pausando com a aba escondida e voltando na hora em que ela reaparece.
 * O cache é por usuário (troca de usuário no mesmo navegador não herda).
 */
const BLOCOS = ["hoje", "mensal", "mes", "resumo", "horarios"];
const INTERVALO_HOJE = 60000;
const memoria = { dono: null, dados: {}, em: null };

function dono() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null")?.id || "anon";
  } catch {
    return "anon";
  }
}

export function useVisaoGeral() {
  if (memoria.dono !== dono()) Object.assign(memoria, { dono: dono(), dados: {}, em: null });
  const [dados, setDados] = useState(() => ({ ...memoria.dados }));
  const [erros, setErros] = useState({});
  const [atualizadoEm, setAtualizadoEm] = useState(memoria.em);
  const [atualizando, setAtualizando] = useState(false);
  const vivo = useRef(true);

  const buscar = useCallback(async (blocos) => {
    await Promise.all(blocos.map(async (b) => {
      try {
        const valor = await visaoGeralApi[b]();
        memoria.dados[b] = valor;
        if (vivo.current) {
          setDados((d) => ({ ...d, [b]: valor }));
          setErros((e) => ({ ...e, [b]: null }));
        }
      } catch (err) {
        if (vivo.current) setErros((e) => ({ ...e, [b]: err }));
      }
    }));
    if (blocos.length > 1) {
      // "Atualizado às" fala da tela inteira; o refresh de 60 s é só do hoje,
      // que mostra a própria hora no painel laranja.
      memoria.em = new Date();
      if (vivo.current) setAtualizadoEm(memoria.em);
    }
  }, []);

  const atualizarTudo = useCallback(async () => {
    setAtualizando(true);
    await buscar(BLOCOS);
    if (vivo.current) setAtualizando(false);
  }, [buscar]);

  // Abre: com memória, mostra e só atualiza o "hoje"; sem memória, busca tudo.
  useEffect(() => {
    vivo.current = true;
    const faltando = BLOCOS.filter((b) => memoria.dados[b] === undefined);
    buscar(faltando.length ? Array.from(new Set([...faltando, "hoje"])) : ["hoje"]);
    return () => { vivo.current = false; };
  }, [buscar]);

  // VENDAS DE HOJE: 60 s, só com a aba visível.
  useEffect(() => {
    let id = null;
    const ligar = () => { if (!id) id = setInterval(() => buscar(["hoje"]), INTERVALO_HOJE); };
    const desligar = () => { if (id) { clearInterval(id); id = null; } };
    const mudou = () => {
      if (document.hidden) desligar();
      else { buscar(["hoje"]); ligar(); }
    };
    if (!document.hidden) ligar();
    document.addEventListener("visibilitychange", mudou);
    return () => { desligar(); document.removeEventListener("visibilitychange", mudou); };
  }, [buscar]);

  return { dados, erros, atualizadoEm, atualizando, atualizarTudo };
}
